import React, { useState, useEffect } from 'react';
import {
  Container,
  Stack,
  Title,
  Tabs,
  Card,
  TextInput,
  Select,
  Button,
  Group,
  Text,
  Badge,
  Switch,
  NumberInput,
  Textarea,
  Paper,
  Loader,
  ScrollArea,
  Checkbox,
  Grid
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconBook,
  IconCards,
  IconCalendar,
  IconSearch,
  IconCheck,
  IconX,
  IconPlus,
  IconClock,
  IconStar
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MobileChildSelector } from './MobileChildSelector';
import { parentApi } from '@/features/parent/api/parentApi';
import { useAuthStore } from '@/features/auth/store/authStore';

interface AssignmentData {
  childId: string;
  type: 'story' | 'flashcards' | 'quiz' | 'theme';
  resourceId?: string;
  title: string;
  description?: string;
  dueDate: Date;
  settings: {
    quickMode?: boolean;
    repeatDaily?: boolean;
    minScore?: number;
    maxAttempts?: number;
    notifyOnComplete?: boolean;
  };
}

export function MobileAssignmentCreator() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);

  // Determine initial tab from URL path
  const getInitialTab = () => {
    if (location.pathname.includes('/flashcards')) return 'flashcards';
    if (location.pathname.includes('/theme')) return 'theme';
    return 'story';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [quickMode, setQuickMode] = useState(true);
  const [settings, setSettings] = useState({
    repeatDaily: false,
    minScore: 80,
    maxAttempts: 3,
    notifyOnComplete: true
  });

  // Fetch children
  const { data: children, isLoading: childrenLoading } = useQuery({
    queryKey: ['children', user?.id],
    queryFn: () => parentApi.getChildren(),
    enabled: !!user?.id
  });

  // Auto-select first child if only one exists
  useEffect(() => {
    if (children && children.length === 1 && !selectedChildId) {
      setSelectedChildId(children[0].id);
    }
  }, [children, selectedChildId]);

  // Fetch available resources based on type
  const { data: resources, isLoading: resourcesLoading } = useQuery({
    queryKey: ['resources', activeTab, searchQuery],
    queryFn: () => {
      switch (activeTab) {
        case 'story':
          return parentApi.getAvailableStories({ search: searchQuery });
        case 'flashcards':
          return parentApi.getAvailableDecks({ search: searchQuery });
        case 'theme':
          return parentApi.getAvailableThemes({ search: searchQuery });
        default:
          return [];
      }
    },
    enabled: !!activeTab
  });

  // Create assignment mutation
  const createAssignment = useMutation({
    mutationFn: (data: AssignmentData) => parentApi.createAssignment(data),
    onSuccess: () => {
      notifications.show({
        title: 'Assignment Created',
        message: 'The assignment has been created successfully',
        color: 'green',
        icon: <IconCheck size={16} />
      });
      navigate('/mobile/parent/dashboard');
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to create assignment. Please try again.',
        color: 'red',
        icon: <IconX size={16} />
      });
    }
  });

  const handleQuickAssign = () => {
    if (!selectedChildId || !selectedResourceId) {
      notifications.show({
        title: 'Missing Information',
        message: 'Please select a child and a resource',
        color: 'yellow'
      });
      return;
    }

    const selectedResource = resources?.find(r => r.id === selectedResourceId);

    // Map assignment type to assignable_type format expected by API
    const assignableTypeMap: Record<string, string> = {
      'story': 'story',
      'flashcards': 'flashcard_deck',
      'theme': 'story_theme'
    };

    // Get the selected child to access their user_id
    const selectedChild = children?.find(c => c.id === selectedChildId);
    if (!selectedChild || !selectedChild.user_id) {
      notifications.show({
        title: 'Error',
        message: 'Selected child not found',
        color: 'red'
      });
      return;
    }

    // Send data in the format the backend expects
    // IMPORTANT: Use user_id for assignee_id (foreign key references users table)
    createAssignment.mutate({
      assignee_id: selectedChild.user_id,  // Use user_id, not student profile id
      assignable_type: assignableTypeMap[activeTab] || 'story',
      assignable_id: selectedResourceId,
      title: selectedResource?.title || 'Assignment',
      instructions: selectedResource?.description,
      due_date: dueDate ? new Date(dueDate).toISOString() : undefined,
      assignment_metadata: quickMode ? { quickMode: true, notifyOnComplete: true } : settings
    } as any);
  };

  return (
    <Container size="sm" p="md">
      <Stack gap="lg">
        {/* Header */}
        <Title order={2}>Create Assignment</Title>

        {/* Child Selector */}
        {childrenLoading ? (
          <Loader size="sm" />
        ) : (
          <MobileChildSelector
            children={children || []}
            selectedChildId={selectedChildId}
            onChildSelect={setSelectedChildId}
          />
        )}

        {/* Assignment Type Tabs */}
        <Tabs value={activeTab} onChange={(value) => {
          setActiveTab(value || 'story');
          setSelectedResourceId(null); // Reset selection when changing tabs
          setSearchQuery(''); // Reset search
        }}>
          <Tabs.List grow>
            <Tabs.Tab value="story" leftSection={<IconBook size={14} />}>
              Story
            </Tabs.Tab>
            <Tabs.Tab value="flashcards" leftSection={<IconCards size={14} />}>
              Flashcards
            </Tabs.Tab>
            <Tabs.Tab value="theme" leftSection={<IconStar size={14} />}>
              Theme
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="story" pt="md">
            <Stack gap="md">
              {/* Quick/Advanced Mode Toggle */}
              <Card p="sm" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={500}>
                      Quick Assignment
                    </Text>
                    <Text size="xs" c="dimmed">
                      Use recommended settings
                    </Text>
                  </div>
                  <Switch
                    checked={quickMode}
                    onChange={(e) => setQuickMode(e.currentTarget.checked)}
                    size="lg"
                  />
                </Group>
              </Card>

              {/* Search Resources */}
              <TextInput
                placeholder={`Search ${activeTab}...`}
                icon={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              {/* Resource Selection */}
              <div>
                <Text size="sm" fw={600} mb="xs">
                  Select {activeTab === 'story' ? 'Story' : activeTab === 'flashcards' ? 'Deck' : 'Theme'}
                </Text>
                <ScrollArea h={200} offsetScrollbars>
                  <Stack gap="xs">
                    {resourcesLoading ? (
                      <Loader size="sm" />
                    ) : resources && resources.length > 0 ? (
                      resources.map((resource) => (
                        <Paper
                          key={resource.id}
                          p="sm"
                          withBorder
                          style={{
                            borderColor: selectedResourceId === resource.id
                              ? 'var(--mantine-color-blue-5)'
                              : undefined,
                            cursor: 'pointer'
                          }}
                          onClick={() => setSelectedResourceId(resource.id)}
                        >
                          <Group justify="space-between">
                            <div style={{ flex: 1 }}>
                              <Text size="sm" fw={500}>
                                {resource.title}
                              </Text>
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {resource.description}
                              </Text>
                              <Group gap="xs" mt={4}>
                                {resource.difficulty && (
                                  <Badge size="xs" variant="light">
                                    {resource.difficulty}
                                  </Badge>
                                )}
                                {resource.cardCount && (
                                  <Badge size="xs" variant="light" color="violet">
                                    {resource.cardCount} cards
                                  </Badge>
                                )}
                                {resource.readTime && (
                                  <Badge size="xs" variant="light" color="blue">
                                    <Group gap={2}>
                                      <IconClock size={10} />
                                      {resource.readTime} min
                                    </Group>
                                  </Badge>
                                )}
                              </Group>
                            </div>
                            <Checkbox
                              checked={selectedResourceId === resource.id}
                              onChange={() => {}}
                              styles={{ input: { cursor: 'pointer' } }}
                            />
                          </Group>
                        </Paper>
                      ))
                    ) : (
                      <Text size="sm" c="dimmed" align="center">
                        No {activeTab} found
                      </Text>
                    )}
                  </Stack>
                </ScrollArea>
              </div>

              {/* Due Date */}
              <DateInput
                label="Due Date (optional)"
                placeholder="No due date"
                value={dueDate}
                onChange={setDueDate}
                minDate={new Date()}
                leftSection={<IconCalendar size={16} />}
                clearable
              />

              {/* Advanced Settings (if not in quick mode) */}
              {!quickMode && (
                <Card p="sm" withBorder>
                  <Stack gap="sm">
                    <Text size="sm" fw={600}>
                      Advanced Settings
                    </Text>

                    <Group justify="space-between">
                      <Text size="sm">Repeat Daily</Text>
                      <Switch
                        checked={settings.repeatDaily}
                        onChange={(e) => setSettings({
                          ...settings,
                          repeatDaily: e.currentTarget.checked
                        })}
                      />
                    </Group>

                    <NumberInput
                      label="Minimum Score (%)"
                      value={settings.minScore}
                      onChange={(val) => setSettings({
                        ...settings,
                        minScore: val || 0
                      })}
                      min={0}
                      max={100}
                      size="sm"
                    />

                    <NumberInput
                      label="Max Attempts"
                      value={settings.maxAttempts}
                      onChange={(val) => setSettings({
                        ...settings,
                        maxAttempts: val || 1
                      })}
                      min={1}
                      max={10}
                      size="sm"
                    />

                    <Group justify="space-between">
                      <Text size="sm">Notify on Complete</Text>
                      <Switch
                        checked={settings.notifyOnComplete}
                        onChange={(e) => setSettings({
                          ...settings,
                          notifyOnComplete: e.currentTarget.checked
                        })}
                      />
                    </Group>
                  </Stack>
                </Card>
              )}

              {/* Submit Button */}
              <Button
                fullWidth
                size="md"
                leftSection={<IconPlus size={18} />}
                onClick={handleQuickAssign}
                loading={createAssignment.isPending}
                disabled={!selectedChildId || !selectedResourceId}
              >
                Create Assignment
              </Button>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="flashcards" pt="md">
            <Stack gap="md">
              {/* Same content as story panel */}
              <Card p="sm" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={500}>
                      Quick Assignment
                    </Text>
                    <Text size="xs" c="dimmed">
                      Use recommended settings
                    </Text>
                  </div>
                  <Switch
                    checked={quickMode}
                    onChange={(e) => setQuickMode(e.currentTarget.checked)}
                    size="lg"
                  />
                </Group>
              </Card>

              <TextInput
                placeholder={`Search ${activeTab}...`}
                icon={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              <div>
                <Text size="sm" fw={600} mb="xs">
                  Select {activeTab === 'story' ? 'Story' : activeTab === 'flashcards' ? 'Deck' : 'Theme'}
                </Text>
                <ScrollArea h={200} offsetScrollbars>
                  <Stack gap="xs">
                    {resourcesLoading ? (
                      <Loader size="sm" />
                    ) : resources && resources.length > 0 ? (
                      resources.map((resource) => (
                        <Paper
                          key={resource.id}
                          p="sm"
                          withBorder
                          style={{
                            borderColor: selectedResourceId === resource.id
                              ? 'var(--mantine-color-blue-5)'
                              : undefined,
                            cursor: 'pointer'
                          }}
                          onClick={() => setSelectedResourceId(resource.id)}
                        >
                          <Group justify="space-between">
                            <div style={{ flex: 1 }}>
                              <Text size="sm" fw={500}>
                                {resource.title}
                              </Text>
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {resource.description}
                              </Text>
                              <Group gap="xs" mt={4}>
                                {resource.difficulty && (
                                  <Badge size="xs" variant="light">
                                    {resource.difficulty}
                                  </Badge>
                                )}
                                {resource.cardCount && (
                                  <Badge size="xs" variant="light" color="violet">
                                    {resource.cardCount} cards
                                  </Badge>
                                )}
                                {resource.readTime && (
                                  <Badge size="xs" variant="light" color="blue">
                                    <Group gap={2}>
                                      <IconClock size={10} />
                                      {resource.readTime} min
                                    </Group>
                                  </Badge>
                                )}
                              </Group>
                            </div>
                            <Checkbox
                              checked={selectedResourceId === resource.id}
                              onChange={() => {}}
                              styles={{ input: { cursor: 'pointer' } }}
                            />
                          </Group>
                        </Paper>
                      ))
                    ) : (
                      <Text size="sm" c="dimmed" align="center">
                        No {activeTab} found
                      </Text>
                    )}
                  </Stack>
                </ScrollArea>
              </div>

              <DateInput
                label="Due Date (optional)"
                placeholder="No due date"
                value={dueDate}
                onChange={setDueDate}
                minDate={new Date()}
                leftSection={<IconCalendar size={16} />}
                clearable
              />

              {!quickMode && (
                <Card p="sm" withBorder>
                  <Stack gap="sm">
                    <Text size="sm" fw={600}>
                      Advanced Settings
                    </Text>
                    <Group justify="space-between">
                      <Text size="sm">Repeat Daily</Text>
                      <Switch
                        checked={settings.repeatDaily}
                        onChange={(e) => setSettings({
                          ...settings,
                          repeatDaily: e.currentTarget.checked
                        })}
                      />
                    </Group>
                    <NumberInput
                      label="Minimum Score (%)"
                      value={settings.minScore}
                      onChange={(val) => setSettings({
                        ...settings,
                        minScore: val || 0
                      })}
                      min={0}
                      max={100}
                      size="sm"
                    />
                    <NumberInput
                      label="Max Attempts"
                      value={settings.maxAttempts}
                      onChange={(val) => setSettings({
                        ...settings,
                        maxAttempts: val || 1
                      })}
                      min={1}
                      max={10}
                      size="sm"
                    />
                    <Group justify="space-between">
                      <Text size="sm">Notify on Complete</Text>
                      <Switch
                        checked={settings.notifyOnComplete}
                        onChange={(e) => setSettings({
                          ...settings,
                          notifyOnComplete: e.currentTarget.checked
                        })}
                      />
                    </Group>
                  </Stack>
                </Card>
              )}

              <Button
                fullWidth
                size="md"
                leftSection={<IconPlus size={18} />}
                onClick={handleQuickAssign}
                loading={createAssignment.isPending}
                disabled={!selectedChildId || !selectedResourceId}
              >
                Create Assignment
              </Button>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="theme" pt="md">
            <Stack gap="md">
              {/* Same content as story panel */}
              <Card p="sm" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={500}>
                      Quick Assignment
                    </Text>
                    <Text size="xs" c="dimmed">
                      Use recommended settings
                    </Text>
                  </div>
                  <Switch
                    checked={quickMode}
                    onChange={(e) => setQuickMode(e.currentTarget.checked)}
                    size="lg"
                  />
                </Group>
              </Card>

              <TextInput
                placeholder={`Search ${activeTab}...`}
                icon={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              <div>
                <Text size="sm" fw={600} mb="xs">
                  Select {activeTab === 'story' ? 'Story' : activeTab === 'flashcards' ? 'Deck' : 'Theme'}
                </Text>
                <ScrollArea h={200} offsetScrollbars>
                  <Stack gap="xs">
                    {resourcesLoading ? (
                      <Loader size="sm" />
                    ) : resources && resources.length > 0 ? (
                      resources.map((resource) => (
                        <Paper
                          key={resource.id}
                          p="sm"
                          withBorder
                          style={{
                            borderColor: selectedResourceId === resource.id
                              ? 'var(--mantine-color-blue-5)'
                              : undefined,
                            cursor: 'pointer'
                          }}
                          onClick={() => setSelectedResourceId(resource.id)}
                        >
                          <Group justify="space-between">
                            <div style={{ flex: 1 }}>
                              <Text size="sm" fw={500}>
                                {resource.title}
                              </Text>
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {resource.description}
                              </Text>
                              <Group gap="xs" mt={4}>
                                {resource.difficulty && (
                                  <Badge size="xs" variant="light">
                                    {resource.difficulty}
                                  </Badge>
                                )}
                                {resource.cardCount && (
                                  <Badge size="xs" variant="light" color="violet">
                                    {resource.cardCount} cards
                                  </Badge>
                                )}
                                {resource.readTime && (
                                  <Badge size="xs" variant="light" color="blue">
                                    <Group gap={2}>
                                      <IconClock size={10} />
                                      {resource.readTime} min
                                    </Group>
                                  </Badge>
                                )}
                              </Group>
                            </div>
                            <Checkbox
                              checked={selectedResourceId === resource.id}
                              onChange={() => {}}
                              styles={{ input: { cursor: 'pointer' } }}
                            />
                          </Group>
                        </Paper>
                      ))
                    ) : (
                      <Text size="sm" c="dimmed" align="center">
                        No {activeTab} found
                      </Text>
                    )}
                  </Stack>
                </ScrollArea>
              </div>

              <DateInput
                label="Due Date (optional)"
                placeholder="No due date"
                value={dueDate}
                onChange={setDueDate}
                minDate={new Date()}
                leftSection={<IconCalendar size={16} />}
                clearable
              />

              {!quickMode && (
                <Card p="sm" withBorder>
                  <Stack gap="sm">
                    <Text size="sm" fw={600}>
                      Advanced Settings
                    </Text>
                    <Group justify="space-between">
                      <Text size="sm">Repeat Daily</Text>
                      <Switch
                        checked={settings.repeatDaily}
                        onChange={(e) => setSettings({
                          ...settings,
                          repeatDaily: e.currentTarget.checked
                        })}
                      />
                    </Group>
                    <NumberInput
                      label="Minimum Score (%)"
                      value={settings.minScore}
                      onChange={(val) => setSettings({
                        ...settings,
                        minScore: val || 0
                      })}
                      min={0}
                      max={100}
                      size="sm"
                    />
                    <NumberInput
                      label="Max Attempts"
                      value={settings.maxAttempts}
                      onChange={(val) => setSettings({
                        ...settings,
                        maxAttempts: val || 1
                      })}
                      min={1}
                      max={10}
                      size="sm"
                    />
                    <Group justify="space-between">
                      <Text size="sm">Notify on Complete</Text>
                      <Switch
                        checked={settings.notifyOnComplete}
                        onChange={(e) => setSettings({
                          ...settings,
                          notifyOnComplete: e.currentTarget.checked
                        })}
                      />
                    </Group>
                  </Stack>
                </Card>
              )}

              <Button
                fullWidth
                size="md"
                leftSection={<IconPlus size={18} />}
                onClick={handleQuickAssign}
                loading={createAssignment.isPending}
                disabled={!selectedChildId || !selectedResourceId}
              >
                Create Assignment
              </Button>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}